import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NbDialogService } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-inventory-dashboard',
  templateUrl: './inventory-dashboard.component.html',
  styleUrls: ['./inventory-dashboard.component.scss']
})
export class InventoryDashboardComponent implements OnInit {

  @Input() storeId: string = '';

  lowStockAlerts: any[] = [];
  inventoryAnalytics: any = null;
  loading: boolean = false;
  
  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalAlerts: number = 0;
  pageSize: number = 50;
  
  // Filters
  selectedSeverity: string = 'all';
  selectedCategory: string = '';
  sortBy: string = 'severity';
  sortOrder: string = 'desc';
  
  // UI State
  showFilters: boolean = false;
  selectedProducts: Set<string> = new Set();
  bulkAction: string = '';
  
  // Bulk Update Modal
  showBulkUpdateModal: boolean = false;
  bulkUpdateForm!: FormGroup;
  
  // Restock Suggestions Modal
  showRestockModal: boolean = false;
  restockSuggestions: any[] = [];
  
  // Categories for filtering
  categories: any[] = [];
  
  // Make Math available in template
  Math = Math;
  
  // Alert severity options
  severityOptions = [
    { value: 'all', label: 'All Alerts' },
    { value: 'critical', label: 'Critical (Out of Stock)' },
    { value: 'warning', label: 'Warning (Low Stock)' },
    { value: 'info', label: 'Info (Approaching Threshold)' }
  ];

  // Bulk operations
  bulkOperations = [
    { value: 'adjust_quantity', label: 'Adjust Quantities' },
    { value: 'update_thresholds', label: 'Update Thresholds' },
    { value: 'restock_suggestions', label: 'Generate Restock Suggestions' }
  ];

  constructor(
    private ecommerceService: EcommerceService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.initializeForms();
    if (this.storeId) {
      this.loadCategories();
      this.loadLowStockAlerts();
      this.loadInventoryAnalytics();
    }
  }

  initializeForms(): void {
    this.bulkUpdateForm = new FormGroup({
      operation: new FormControl('adjust_quantity', [Validators.required]),
      adjustmentType: new FormControl('add', [Validators.required]),
      adjustmentValue: new FormControl(50, [Validators.required, Validators.min(1)]),
      newThreshold: new FormControl(25, [Validators.min(0)]),
      thresholdType: new FormControl('fixed', [Validators.required])
    });
  }

  loadCategories(): void {
    this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).subscribe(
      (res: any) => {
        if (res.response && res.response.success && res.response.data) {
          this.categories = res.response.data.categories || [];
        } else if (res.code === 200 && res.data) {
          this.categories = res.data.categories || [];
        }
      },
      (error) => {
        console.error('Failed to load categories:', error);
      }
    );
  }

  loadLowStockAlerts(): void {
    this.loading = true;
    
    const params = {
      severity: this.selectedSeverity === 'all' ? '' : this.selectedSeverity,
      page: this.currentPage,
      limit: this.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...(this.selectedCategory && { categoryId: this.selectedCategory })
    };

    this.ecommerceService.getLowStockAlerts(this.storeId, params).subscribe(
      (res: any) => {
        console.log('Low stock alerts response:', res);
        
        if (res.response && res.response.success && res.response.data) {
          this.lowStockAlerts = res.response.data.alerts || [];
          this.totalAlerts = res.response.data.summary?.totalAlerts || 0;
        } else if (res.code === 200 && res.data) {
          this.lowStockAlerts = res.data.alerts || [];
          this.totalAlerts = res.data.summary?.totalAlerts || 0;
        } else {
          console.warn('Unexpected alerts response format:', res);
          this.lowStockAlerts = [];
        }
        this.loading = false;
      },
      (error) => {
        console.error('Failed to load low stock alerts:', error);
        this.toastr.error('Failed to load inventory alerts');
        this.loading = false;
      }
    );
  }

  loadInventoryAnalytics(): void {
    this.ecommerceService.getInventoryAnalytics(this.storeId).subscribe(
      (res: any) => {
        console.log('Inventory analytics response:', res);
        
        if (res.response && res.response.success && res.response.data) {
          this.inventoryAnalytics = res.response.data;
        } else if (res.code === 200 && res.data) {
          this.inventoryAnalytics = res.data;
        }
      },
      (error) => {
        console.error('Failed to load inventory analytics:', error);
      }
    );
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadLowStockAlerts();
  }

  onSortChange(): void {
    this.loadLowStockAlerts();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLowStockAlerts();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  clearFilters(): void {
    this.selectedSeverity = 'all';
    this.selectedCategory = '';
    this.onFilterChange();
  }

  toggleProductSelection(productId: string): void {
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
    } else {
      this.selectedProducts.add(productId);
    }
  }

  selectAllProducts(): void {
    if (this.selectedProducts.size === this.lowStockAlerts.length) {
      this.selectedProducts.clear();
    } else {
      this.lowStockAlerts.forEach(alert => {
        this.selectedProducts.add(alert._id);
      });
    }
  }

  openBulkUpdateModal(): void {
    if (this.selectedProducts.size === 0) {
      this.toastr.warning('Please select products first');
      return;
    }
    this.showBulkUpdateModal = true;
  }

  closeBulkUpdateModal(): void {
    this.showBulkUpdateModal = false;
    this.bulkUpdateForm.reset();
    this.bulkUpdateForm.patchValue({
      operation: 'adjust_quantity',
      adjustmentType: 'add',
      adjustmentValue: 50,
      newThreshold: 25,
      thresholdType: 'fixed'
    });
  }

  executeBulkUpdate(): void {
    if (this.bulkUpdateForm.invalid) {
      this.markFormGroupTouched(this.bulkUpdateForm);
      this.toastr.error('Please fill in all required fields');
      return;
    }

    const formValue = this.bulkUpdateForm.value;
    const selectedProductIds = Array.from(this.selectedProducts);
    
    let bulkData: any = {
      operation: formValue.operation
    };

    switch (formValue.operation) {
      case 'adjust_quantity':
        bulkData.filters = { productIds: selectedProductIds };
        bulkData.updates = {
          adjustmentType: formValue.adjustmentType,
          adjustmentValue: formValue.adjustmentValue
        };
        break;
      
      case 'update_thresholds':
        bulkData.filters = { productIds: selectedProductIds };
        bulkData.updates = {
          newThreshold: formValue.newThreshold,
          thresholdType: formValue.thresholdType
        };
        break;
      
      case 'restock_suggestions':
        bulkData = { operation: 'restock_suggestions' };
        break;
    }

    this.performBulkUpdate(bulkData);
  }

  performBulkUpdate(bulkData: any): void {
    this.spinner.show();
    
    this.ecommerceService.bulkUpdateInventory(this.storeId, bulkData).subscribe(
      (res: any) => {
        console.log('Bulk update response:', res);
        
        if (bulkData.operation === 'restock_suggestions') {
          // Handle restock suggestions
          if (res.response && res.response.success && res.response.data) {
            this.restockSuggestions = res.response.data.suggestions || [];
            this.showRestockModal = true;
          } else if (res.code === 200 && res.data) {
            this.restockSuggestions = res.data.suggestions || [];
            this.showRestockModal = true;
          }
          this.toastr.success('Restock suggestions generated successfully');
        } else {
          // Handle other bulk operations
          if ((res.response && res.response.success) || res.code === 200) {
            this.toastr.success(`Bulk ${bulkData.operation.replace('_', ' ')} completed successfully`);
            this.loadLowStockAlerts();
            this.loadInventoryAnalytics();
            this.selectedProducts.clear();
          }
        }
        
        this.closeBulkUpdateModal();
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to execute bulk update:', error);
        this.toastr.error('Failed to update inventory: ' + (error.error?.message || error.message));
        this.spinner.hide();
      }
    );
  }

  closeRestockModal(): void {
    this.showRestockModal = false;
    this.restockSuggestions = [];
  }

  applyRestockSuggestions(): void {
    const restockData = {
      operation: 'apply_suggestions',
      suggestions: this.restockSuggestions.map(suggestion => ({
        productId: suggestion.productId,
        newQuantity: suggestion.currentStock + suggestion.suggestedReorder
      }))
    };

    this.spinner.show();
    
    this.ecommerceService.bulkUpdateInventory(this.storeId, restockData).subscribe(
      (res: any) => {
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Restock suggestions applied successfully');
          this.loadLowStockAlerts();
          this.loadInventoryAnalytics();
          this.closeRestockModal();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to apply restock suggestions:', error);
        this.toastr.error('Failed to apply suggestions');
        this.spinner.hide();
      }
    );
  }

  quickRestock(alert: any): void {
    const recommendedAmount = alert.alertInfo?.recommendedReorder || 50;
    
    Swal.fire({
      title: 'Quick Restock',
      text: `Add ${recommendedAmount} units to ${alert.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, restock',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performQuickRestock(alert._id, recommendedAmount);
      }
    });
  }

  performQuickRestock(productId: string, quantity: number): void {
    const bulkData = {
      operation: 'adjust_quantity',
      filters: { productIds: [productId] },
      updates: {
        adjustmentType: 'add',
        adjustmentValue: quantity
      }
    };

    this.spinner.show();
    
    this.ecommerceService.bulkUpdateInventory(this.storeId, bulkData).subscribe(
      (res: any) => {
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success(`Successfully restocked with ${quantity} units`);
          this.loadLowStockAlerts();
          this.loadInventoryAnalytics();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to restock:', error);
        this.toastr.error('Failed to restock product');
        this.spinner.hide();
      }
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  getSeverityColor(severity: string): string {
    switch (severity) {
      case 'critical': return 'danger';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'basic';
    }
  }

  getSeverityIcon(severity: string): string {
    switch (severity) {
      case 'critical': return 'alert-triangle-outline';
      case 'warning': return 'alert-circle-outline';
      case 'info': return 'info-outline';
      default: return 'checkmark-circle-outline';
    }
  }

  formatCurrency(amount: number): string {
    if (!amount) return '0.00 DKK';
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK'
    }).format(amount);
  }

  trackByAlertId(index: number, alert: any): string {
    return alert._id;
  }

  getTotalRestockCost(): number {
    return this.restockSuggestions.reduce((sum, suggestion) => sum + suggestion.estimatedCost, 0);
  }
}